import { useEffect, useRef, useState } from "react";
import { FileText, Loader2 } from "lucide-react";

import api from "../../services/api";

// Primeira página do PDF dentro da bolha da conversa.
//
// Sem isto, dez anexos seguidos viravam dez cartões idênticos escritos
// "Arquivo PDF" — para saber qual era a planta e qual era o orçamento, a
// pessoa tinha que abrir um por um.
//
// É um <iframe> com o blob, e não uma imagem: não há biblioteca de PDF no
// projeto, e o próprio visualizador do navegador desenha a página. O blob é
// obrigatório por dois motivos — a requisição precisa do token, e os uploads
// são servidos com `Content-Security-Policy: sandbox`, que deixa o iframe em
// branco quando se aponta direto para a URL.
//
// Só carrega quando entra na tela, e DESCARREGA ao sair: cada iframe de PDF é
// uma instância do leitor do navegador, e numa conversa com dezenas de anexos
// manter todas vivas ao mesmo tempo consome memória à toa.
const MiniaturaDePdf = ({ url, onAbrir }) => {
  const caixaRef = useRef(null);
  const [visivel, setVisivel] = useState(false);
  const [blob, setBlob] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    const alvo = caixaRef.current;
    if (!alvo) return undefined;

    // A margem faz a página já estar pronta quando a rolagem chega nela.
    const observador = new IntersectionObserver(
      ([entrada]) => setVisivel(entrada.isIntersecting),
      { rootMargin: "300px" },
    );

    observador.observe(alvo);
    return () => observador.disconnect();
  }, []);

  useEffect(() => {
    if (!visivel || !url) return undefined;

    let criada = "";
    let cancelado = false;

    const carregar = async () => {
      setErro(false);

      try {
        const { data } = await api.get(url, { responseType: "blob" });
        if (cancelado) return;

        criada = window.URL.createObjectURL(
          new Blob([data], { type: "application/pdf" }),
        );
        setBlob(criada);
      } catch {
        if (!cancelado) setErro(true);
      }
    };

    carregar();

    return () => {
      cancelado = true;
      setBlob(null);
      if (criada) window.URL.revokeObjectURL(criada);
    };
  }, [visivel, url]);

  return (
    <button
      type="button"
      ref={caixaRef}
      onClick={onAbrir}
      title="Abrir o documento"
      className="relative block h-[150px] w-[230px] overflow-hidden rounded-lg border bg-white"
    >
      {blob && !erro ? (
        <>
          {/* `#toolbar=0…` tira a barra do leitor: aqui é miniatura, não
              leitura. O pointer-events-none entrega o clique ao botão — sem
              ele o iframe engole o clique e o documento não abre. */}
          <iframe
            src={`${blob}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            title="Primeira página do documento"
            tabIndex={-1}
            className="pointer-events-none h-[300px] w-[460px] origin-top-left scale-50 border-0"
          />
          <span className="absolute inset-x-0 bottom-0 bg-black/55 px-2 py-0.5 text-left text-[10px] text-white">
            PDF · clique para abrir
          </span>
        </>
      ) : (
        <span className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
          {erro ? (
            <>
              <FileText className="h-8 w-8" />
              <span className="text-[11px]">Não deu para pré-visualizar</span>
            </>
          ) : (
            <Loader2 className="h-6 w-6 animate-spin" />
          )}
        </span>
      )}
    </button>
  );
};

export default MiniaturaDePdf;
